import type { EnemySpec } from '../core/enemy';
import { TOWER_MAX_HP } from '../core/fireball';

/**
 * Plain-English enemy descriptions — the mirror of towerInfo.ts, read by the
 * Test Arena's enemy guide. No jargon: these are read by players, not designers.
 */

/** Core stat line for an enemy: health · speed · bounty. */
export function enemyStatLine(s: EnemySpec): string {
  return [`Health ${s.hp}`, `speed ${s.speed}`, `pays $${s.bounty}`].join(' · ');
}

/**
 * Special-ability summary, in plain words. Empty string for a plain enemy that
 * just walks — the guide hides the line entirely in that case.
 */
export function enemySpecial(s: EnemySpec): string {
  const parts: string[] = [];
  if (s.stun)
    parts.push(`disables a random tower for ${s.stun.duration}s, every ${s.stun.cooldown}s`);
  if (s.fireRateHex)
    parts.push(
      `slows a random tower's fire rate ${Math.round(s.fireRateHex.slow * 100)}% ` +
        `for ${s.fireRateHex.duration}s, every ${s.fireRateHex.cooldown}s`,
    );
  if (s.placementLock)
    parts.push(
      `seals tower placement for ${s.placementLock.duration}s, ` +
        `every ${s.placementLock.cooldown}s`,
    );
  if (s.summon) {
    const n = s.summon.maxAlive;
    const who = n === 1 ? 'a minion' : `up to ${n} minions`;
    const where = s.summon.fromBase ? 'from the entrance' : 'from itself';
    parts.push(`summons ${who} ${where}, every ${s.summon.cooldown}s`);
  }
  if (s.towerFire?.poison)
    parts.push(
      `poisons a random tower every ${s.towerFire.cooldown}s: ` +
        `${s.towerFire.poison.damage} HP a second for ${s.towerFire.poison.ticks}s`,
    );
  else if (s.towerFire?.possess)
    parts.push(
      `shroomnitizes a random tower every ${s.towerFire.cooldown}s: it stops attacking ` +
        `and hits another of your towers for ${s.towerFire.possess.damage} HP`,
    );
  else if (s.towerFire)
    parts.push(
      `gives every tower ${TOWER_MAX_HP} HP, then burns ${s.towerFire.damage} HP off ` +
        `a random tower every ${s.towerFire.cooldown}s — a tower at 0 is destroyed`,
    );
  if (s.dodge) parts.push(`dodges ${Math.round(s.dodge.chance * 100)}% of tower hits`);
  if (s.slowImmune) parts.push('cannot be slowed or frozen');
  if (s.livesCost && s.livesCost > 1) parts.push(`costs ${s.livesCost} lives if it gets through`);
  return parts.join(' · ');
}
